import { z } from "zod";
import { settingsStore, type CustomField } from "../stores/settings";
import { userStore, setCustomFields, type UserStore } from "../stores/user";
import { get } from "svelte/store";
import { getCountries, isValidNumberForRegion } from "./libphonenumber";
import type { CountryCode } from "libphonenumber-js";
import { IMask } from "@imask/svelte";
import { saveUserSettings, updateUser } from "../apiLoader/user";
import { NavigationType, type Route } from "../stores/session";
import { navigateTo, navigateToPath } from "../../lib/routing";
import PATHS from "../../lib/constants/routePaths";
import isEmpty from "lodash/isEmpty";
import pickBy from "lodash/pickBy";
import { processIssues } from "./validation_utils";
import { differenceInYears, parse } from "date-fns";

// This RegEx is to prevent characters unsupported by Doordash on the name fields
// This validation also happens on the backend, this is just to catch the issue earlier for the user
const illegalNameCharacters = /^[^(`@#$%^&*+=?;[{\]})]*$/;

export const CountryPhoneHelper = {
  getCountries: (): CountryCode[] => {
    const countries = (get(settingsStore).countries as string[]) || [];
    return countries.includes("world") || countries.length === 0
      ? getCountries()
      : (countries.map((country) => country.toUpperCase()) as CountryCode[]);
  }
};

export const PhoneValidator = {
  isValid: (phone: string) => {
    const countries = CountryPhoneHelper.getCountries();
    return countries.reduce((acc, country) => acc || isValidNumberForRegion(phone, country), false);
  }
};

export type UserData = {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
};

export const getPhoneMask = () => {
  const countries = CountryPhoneHelper.getCountries();
  const isUSorCA = countries.reduce((acc, country) => acc && ["US", "CA"].includes(country), true);
  return isUSorCA ? "(000) 000 0000" : undefined;
};

export const getMaskedPhone = (phone: string | null | undefined = "") => {
  if (!phone) {
    return "";
  }

  const mask = getPhoneMask();
  if (mask) {
    const phoneMask = IMask.createMask({ mask });
    phoneMask.resolve(phone);
    return phoneMask.value;
  } else {
    // Return the original phone if we don't have any mask
    return phone;
  }
};

export type UserDetailsFieldNames = "first_name" | "last_name" | "email" | "phone";

type ValidationOptions = {
  disablePhone?: boolean;
};

const defaultValidationOptions: ValidationOptions = {
  disablePhone: false
};

const getEmailValidator = (copy: Record<string, string>) => {
  const requiredUserEmailPattern: string = (get(settingsStore).requiredUserEmailPattern as string) || "";

  const baseEmailValidator = z.string().trim().min(1, { message: copy.emailEmpty }).email(copy.emailInvalid);

  return requiredUserEmailPattern.length
    ? baseEmailValidator.refine((email) => email.endsWith(requiredUserEmailPattern), {
      message: `${copy.emailPatternInvalid} ${requiredUserEmailPattern}`
    })
    : baseEmailValidator;
};

const getFieldValidators = (copy: Record<string, string>, options: ValidationOptions = defaultValidationOptions) => {
  return {
    first_name: z
      .string()
      .trim()
      .min(1, { message: copy.firstNameEmpty })
      .min(2, { message: copy.firstNameInvalid })
      .regex(illegalNameCharacters, { message: copy.firstNameContainsIllegalCharacters }),
    last_name: z
      .string()
      .trim()
      .min(1, { message: copy.lastNameEmpty })
      .min(2, { message: copy.lastNameInvalid })
      .regex(illegalNameCharacters, { message: copy.lastNameContainsIllegalCharacters }),
    email: getEmailValidator(copy),
    phone: options?.disablePhone
      ? z.string().optional().nullable()
      : z
        .string()
        .trim()
        .min(1, { message: copy.phoneEmpty })
        .refine((phone) => PhoneValidator.isValid(phone), { message: copy.phoneInvalid }),
    entryToken: checkIsEntryTokenRequired()
      ? z.string().trim().min(1, { message: copy.entryTokenEmpty })
      : z.string().optional()
  };
};

export const FieldsValidationHelper = {
  validateAllFields: (
    userData: UserData,
    copy: Record<string, string>,
    options: ValidationOptions = defaultValidationOptions
  ) => {
    const UserDataSchema = z.object(getFieldValidators(copy, options));
    const { success, data, error } = UserDataSchema.safeParse(userData);
    return { success, data, issues: error ? processIssues(error.issues) : {} };
  }
};

export const FullDataValidator = {
  validateAllData: (
    copy: Record<string, string>,
    customFields: CustomField[] = [],
    options: ValidationOptions = defaultValidationOptions
  ) => {
    const fieldValidations = FieldsValidationHelper.validateAllFields(get(userStore).user, copy, options);

    return {
      fieldValidations: fieldValidations,
      customFieldsValidations: CustomFieldsHelper.validateCustomFields(customFields)
    };
  },
  validateAndSubmit: async (
    copy: Record<string, string>,
    customFields: CustomField[] = [],
    options: ValidationOptions = defaultValidationOptions
  ) => {
    const { fieldValidations, customFieldsValidations } = FullDataValidator.validateAllData(
      copy,
      customFields,
      options
    );

    if (fieldValidations.success && customFieldsValidations.success && fieldValidations.data) {
      try {
        // get all custom field names that are not checkboxes
        const nonCheckBoxCustomFieldNames = customFields
          .filter((field) => field.type !== "checkbox")
          .map((field) => field.name);

        // pick the validated data
        const nonCheckboxRecords = pickBy(customFieldsValidations.data, (_, key) =>
          nonCheckBoxCustomFieldNames.includes(key)
        );

        const userData = pickBy(
          {
            ...fieldValidations.data,
            ...nonCheckboxRecords
          },
          Boolean
        ) as UserData;
        const checkboxData = pickBy(
          customFieldsValidations.data,
          (_, key) => !nonCheckBoxCustomFieldNames.includes(key)
        );

        await Promise.all([updateUser(userData), !isEmpty(checkboxData) ? saveUserSettings(checkboxData) : null]);

        if (checkIsEntryTokenRequired()) {
          await saveUserSettings({ entry_token: get(userStore).user?.entryToken });
        }

        navigateTo(NavigationType.Next);
      } catch (error) {
        navigateToPath(PATHS.ERROR);
      }
    }

    return { fieldValidations, customFieldsValidations };
  }
};

export const CustomFieldsHelper = {
  setupCustomFields: (customFields: CustomField[] = []) => {
    const currentCustomFields = get(userStore).customFields;

    const newCustomFields = customFields.reduce((acc, { name, default_value: defaultValue }) => {
      const currentCustomField = currentCustomFields[name];
      acc[name] = currentCustomField === undefined ? defaultValue : currentCustomField;
      return acc;
    }, {} as Record<string, boolean | string>);

    setCustomFields(newCustomFields);
  },
  validateCustomFields: (customFields: CustomField[] = []) => {
    const currentCustomFields = get(userStore).customFields;

    const issues = customFields.reduce((acc, customField) => {
      acc[customField.name] = customFieldValidator(currentCustomFields, customField);
      return acc;
    }, {} as Record<string, ValidationErrorType | undefined>);

    return { success: Object.values(issues).every((value) => !value), data: currentCustomFields, issues };
  }
};

export enum ValidationErrorType {
  Missing = "missing",
  Invalid = "invalid"
}

const customFieldValidator = (values: Record<string, string | boolean>, customField: CustomField) => {
  const hasValue = typeof values?.[customField.name] === "string"
    ? String(values?.[customField.name])?.length > 0
    : values?.[customField.name];

  if (customField.is_required && !hasValue) {
    return ValidationErrorType.Missing;
  } else if (customField.validation === "regexp" && customField.type === "text_input" && hasValue) {
    // nosemgrep
    const expression = new RegExp(customField.validation_config);
    // nosemgrep
    const result = expression.test(values[customField.name] as string);
    if (!result) {
      return ValidationErrorType.Invalid;
    }
  } else if (customField.type === "date_input" && customField.validation === "dob" && hasValue) {
    const ageLimit = Number(customField.validation_config);

    // We save the date in the format of yyyy-MM-dd, but we need to make sure we can handle
    // this format in safari hence we use parse
    // https://date-fns.org/v2.0.0/docs/parse
    const date = parse(values[customField.name] as string, "yyyy-MM-dd", new Date());
    const today = new Date();

    if (differenceInYears(today, date) < ageLimit) {
      return ValidationErrorType.Invalid;
    }
  }
};

// We will hide the back button if the flow skipped the landing page
export const BackbuttonHelper = {
  showBackButton: (v2Settings: { flow: Route[] }) => {
    return !v2Settings.flow.find((route) => route.component === "SkippedLanding");
  }
};

export const checkIsEntryTokenRequired = () => {
  return get(settingsStore).entry_validations?.some((validation) => validation.name === "SingleUseToken");
};

export const initialFieldValidation = (
  userStore: UserStore,
  disablePhone: boolean,
  customFields: CustomField[] = []
) => {
  const requiredCustomFieldNames = customFields.filter((field) => field.is_required).map((field) => field.name);
  const requiredFields = ["first_name", "last_name", "email"];
  const fields = [...requiredFields, ...requiredCustomFieldNames];

  if (!disablePhone) {
    fields.push("phone");
  }

  const inputs = {
    ...userStore.user,
    ...userStore.customFields
  };

  return fields.every((field) => Boolean(inputs[field]));
};

export const getCustomCopy = (
  customField: CustomField,
  issue: "missing" | "invalid" | undefined,
  copy: Record<string, string>
) => {
  if (issue === ValidationErrorType.Missing) {
    return copy[`customField-${customField.identifier}RequiredCopy`] ?? copy.customFieldRequiredCopy;
  } else if (issue === ValidationErrorType.Invalid) {
    return copy[`customField-${customField.identifier}InvalidCopy`] ?? copy.customFieldInvalidCopy;
  }
};
